import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useInitializeLearning, useSkillProfile, useUpdateSkillProfile } from '@/hooks/use-learning'
import type { LearningGoal, SkillLevel } from '@/types'
import {
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Check,
  Clock,
  Rocket,
  Search,
  TrendingUp,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const GOALS = [
  {
    value: 'new_job' as LearningGoal,
    label: 'Land a new job',
    description: 'Find and land my next role',
    icon: Briefcase,
  },
  {
    value: 'career_change' as LearningGoal,
    label: 'Change careers',
    description: 'Transition to a completely different field',
    icon: Rocket,
  },
  {
    value: 'level_up' as LearningGoal,
    label: 'Level up skills',
    description: 'Advance in my current career path',
    icon: TrendingUp,
  },
  {
    value: 'exploring' as LearningGoal,
    label: 'Just exploring',
    description: 'See what skills I might want to learn',
    icon: Search,
  },
]

const ROLE_SUGGESTIONS = [
  'Project Manager',
  'Registered Nurse',
  'Marketing Manager',
  'Sales Representative',
  'Accountant',
  'Data Analyst',
  'Software Engineer',
  'Teacher',
  'Operations Manager',
  'Human Resources Manager',
  'Financial Analyst',
  'Customer Success Manager',
  'Graphic Designer',
  'Social Worker',
  'Supply Chain Manager',
  'Executive Assistant',
]

// Skills for exploration mode - grouped by category
const EXPLORE_SKILLS = [
  { name: 'Communication', category: 'Soft Skills' },
  { name: 'Leadership', category: 'Soft Skills' },
  { name: 'Public Speaking', category: 'Soft Skills' },
  { name: 'Negotiation', category: 'Soft Skills' },
  { name: 'Time Management', category: 'Soft Skills' },
  { name: 'Project Management', category: 'Business' },
  { name: 'Marketing', category: 'Business' },
  { name: 'Sales', category: 'Business' },
  { name: 'Accounting', category: 'Business' },
  { name: 'Operations', category: 'Business' },
  { name: 'Excel', category: 'Technical' },
  { name: 'Data Analysis', category: 'Technical' },
  { name: 'SQL', category: 'Technical' },
  { name: 'Python', category: 'Technical' },
  { name: 'Graphic Design', category: 'Creative' },
  { name: 'Writing', category: 'Creative' },
]

const TIME_OPTIONS = [
  { value: 70, label: '5-10 min/day', description: 'Quick daily learning' },
  { value: 150, label: '15-30 min/day', description: 'Steady progress' },
  { value: 420, label: '1+ hour/day', description: 'Intensive learning' },
]

type Step = 'goal' | 'role' | 'skills' | 'time'

export function LearnOnboardPage() {
  const navigate = useNavigate()
  const { data: profile, isLoading: profileLoading } = useSkillProfile()
  const updateProfile = useUpdateSkillProfile()
  const initializeLearning = useInitializeLearning()

  const [step, setStep] = useState<Step>('goal')
  const [goal, setGoal] = useState<LearningGoal | null>(null)
  const [targetRole, setTargetRole] = useState('')
  const [selectedSkills, setSelectedSkills] = useState<string[]>([])
  const [weeklyTime, setWeeklyTime] = useState<number | null>(null)
  const [initialized, setInitialized] = useState(false)

  // Sync state with profile data when it loads
  useEffect(() => {
    if (profile && !initialized) {
      setInitialized(true)
      if (profile.learning_goal) {
        setGoal(profile.learning_goal)
      }
      if (profile.target_role) {
        setTargetRole(profile.target_role)
      }
      if (profile.target_skills) {
        // Extract skill names from target_skills object
        setSelectedSkills(Object.keys(profile.target_skills))
      }
      if (profile.weekly_time_goal) {
        setWeeklyTime(profile.weekly_time_goal)
      }
    }
  }, [profile, initialized])

  const isExploring = goal === 'exploring'
  const steps: Step[] = isExploring ? ['goal', 'skills', 'time'] : ['goal', 'role', 'time']

  const handleNext = () => {
    // Only used for role and skills steps (goal and time auto-advance)
    if (step === 'role' || step === 'skills') {
      setStep('time')
    }
  }

  const handleBack = () => {
    if (step === 'role' || step === 'skills') {
      setStep('goal')
    } else if (step === 'time') {
      setStep(isExploring ? 'skills' : 'role')
    }
  }

  const toggleSkill = (skill: string) => {
    setSelectedSkills((prev) =>
      prev.includes(skill) ? prev.filter((s) => s !== skill) : [...prev, skill],
    )
  }

  const handleCompleteWithTime = (time: number) => {
    // Build update object conditionally to avoid undefined values
    const update: Parameters<typeof updateProfile.mutate>[0] = {
      learning_goal: goal,
      target_role: isExploring ? null : targetRole || null,
      weekly_time_goal: time,
    }

    // Add target_skills only for exploring mode with selected skills
    if (isExploring && selectedSkills.length > 0) {
      update.target_skills = Object.fromEntries(
        selectedSkills.map((skill) => [skill, 'beginner' as SkillLevel]),
      )
    }

    updateProfile.mutate(update, {
      onSuccess: () => {
        initializeLearning.mutate()
        navigate('/learn')
      },
    })
  }

  // Only needed for role and skills steps now
  const showNextButton = step === 'role' || step === 'skills'

  // Show loading while profile loads
  if (profileLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="w-full max-w-2xl mx-auto px-6 py-12">
        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  step === s
                    ? 'bg-primary text-primary-foreground'
                    : steps.indexOf(step) > i
                      ? 'bg-primary/20 text-primary'
                      : 'bg-muted text-muted-foreground'
                }`}
              >
                {i + 1}
              </div>
              {i < steps.length - 1 && <div className="w-12 h-0.5 bg-muted mx-1" />}
            </div>
          ))}
        </div>

        {/* Step 1: Goal - auto-advance on selection */}
        {step === 'goal' && (
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">What's your goal?</CardTitle>
              <CardDescription>This helps us personalize your learning experience</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {GOALS.map((g) => {
                const Icon = g.icon
                return (
                  <button
                    type="button"
                    key={g.value}
                    onClick={() => {
                      setGoal(g.value)
                      // Auto-advance after brief visual feedback
                      setTimeout(() => {
                        setStep(g.value === 'exploring' ? 'skills' : 'role')
                      }, 150)
                    }}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-all flex items-start gap-4 ${
                      goal === g.value
                        ? 'border-primary bg-primary/5'
                        : 'border-muted hover:border-muted-foreground/30'
                    }`}
                  >
                    <div
                      className={`p-2 rounded-lg ${
                        goal === g.value ? 'bg-primary text-primary-foreground' : 'bg-muted'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-medium">{g.label}</div>
                      <div className="text-sm text-muted-foreground">{g.description}</div>
                    </div>
                  </button>
                )
              })}
            </CardContent>
          </Card>
        )}

        {/* Step 2: Role */}
        {step === 'role' && (
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">What role interests you?</CardTitle>
              <CardDescription>
                We'll tailor content to help you reach this goal (optional)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                placeholder="e.g., Project Manager, Nurse, Teacher..."
                value={targetRole}
                onChange={(e) => setTargetRole(e.target.value)}
                className="text-lg py-6"
              />
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Popular roles:</p>
                <div className="flex flex-wrap gap-2">
                  {ROLE_SUGGESTIONS.map((role) => (
                    <button
                      type="button"
                      key={role}
                      onClick={() => setTargetRole(role)}
                      className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                        targetRole === role
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                      }`}
                    >
                      {role}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2b: Skills (for exploring mode) */}
        {step === 'skills' && (
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">What interests you?</CardTitle>
              <CardDescription>
                Pick any skills you'd like to explore (you can change this later)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {['Soft Skills', 'Business', 'Technical', 'Creative'].map((category) => {
                const categorySkills = EXPLORE_SKILLS.filter((s) => s.category === category)
                if (categorySkills.length === 0) return null
                return (
                  <div key={category}>
                    <p className="text-sm text-muted-foreground mb-2">{category}</p>
                    <div className="flex flex-wrap gap-2">
                      {categorySkills.map((skill) => {
                        const isSelected = selectedSkills.includes(skill.name)
                        return (
                          <button
                            type="button"
                            key={skill.name}
                            onClick={() => toggleSkill(skill.name)}
                            className={`px-3 py-1.5 text-sm rounded-full transition-colors flex items-center gap-1.5 ${
                              isSelected
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                            }`}
                          >
                            {isSelected && <Check className="h-3 w-3" />}
                            {skill.name}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
              {selectedSkills.length > 0 && (
                <p className="text-sm text-primary">
                  {selectedSkills.length} skill{selectedSkills.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Time - auto-complete on selection */}
        {step === 'time' && (
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">How much time can you commit?</CardTitle>
              <CardDescription>We'll pace your learning accordingly</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {TIME_OPTIONS.map((t) => (
                <button
                  type="button"
                  key={t.value}
                  onClick={() => {
                    setWeeklyTime(t.value)
                    // Auto-complete after brief visual feedback
                    setTimeout(() => {
                      handleCompleteWithTime(t.value)
                    }, 150)
                  }}
                  disabled={updateProfile.isPending}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all flex items-center gap-4 ${
                    weeklyTime === t.value
                      ? 'border-primary bg-primary/5'
                      : 'border-muted hover:border-muted-foreground/30'
                  } ${updateProfile.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div
                    className={`p-2 rounded-lg ${
                      weeklyTime === t.value ? 'bg-primary text-primary-foreground' : 'bg-muted'
                    }`}
                  >
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-medium">{t.label}</div>
                    <div className="text-sm text-muted-foreground">{t.description}</div>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Navigation buttons - Back for all except goal, Next only for role/skills */}
        {step !== 'goal' && (
          <div className="flex justify-between mt-6">
            <Button variant="outline" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>

            {showNextButton ? (
              <Button onClick={handleNext}>
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <div /> // Spacer for flex justify-between
            )}
          </div>
        )}

        {/* Skip option */}
        <div className="text-center mt-4">
          <button
            type="button"
            onClick={() => navigate('/learn')}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  )
}
